import React from 'react';
import { chakra, Heading } from '@chakra-ui/react';
import type { HeadingProps } from '@chakra-ui/react';

// =============================================================================
// OMNI AppHeader Heading Component
// =============================================================================
// Universal heading component using OMNI AppHeader recipe for consistent
// brand title styling with proper OMNI typography and responsive design
// =============================================================================

// OMNI Brand Heading
// =============================================================================
// Main brand title with OMNI typography and responsive scaling
export interface OmniBrandHeadingProps extends Omit<HeadingProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'accent' | 'contrast';
  size?: 'sm' | 'md' | 'lg';
}

// Create OMNI Brand Heading using factory pattern
const OmniBrandHeadingBase = chakra(Heading, {
  base: {
    color: 'omni.text.primary',
    fontWeight: '600',
    lineHeight: '1.2',
    m: 0,
    userSelect: 'none',
    fontSize: { base: 'md', sm: 'lg', md: 'xl', lg: '2xl', xl: '3xl' },
  },
  variants: {
    variant: {
      primary: {
        color: 'omni.text.primary',
      },
      secondary: {
        color: 'omni.text.secondary',
      },
      accent: {
        color: 'omni.primary.500',
      },
      contrast: {
        // High contrast variant for headers without background
        color: 'gray.900',
        textShadow:
          '0 1px 2px rgba(255, 255, 255, 0.8), 0 0 4px rgba(255, 255, 255, 0.6)',
        fontWeight: '700',
        letterSpacing: '0.025em',
      },
    },
    size: {
      sm: {
        fontSize: { base: 'sm', sm: 'md', md: 'lg' },
      },
      md: {
        fontSize: { base: 'md', sm: 'lg', md: 'xl', lg: '2xl' },
      },
      lg: {
        fontSize: { base: 'lg', sm: 'xl', md: '2xl', lg: '3xl', xl: '4xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export const OmniBrandHeading = React.forwardRef<
  HTMLHeadingElement,
  OmniBrandHeadingProps
>((props, ref) => {
  const { variant = 'primary', size = 'md', ...rest } = props;

  return (
    <OmniBrandHeadingBase ref={ref} variant={variant} size={size} {...rest} />
  );
});

OmniBrandHeading.displayName = 'OmniBrandHeading';

// =============================================================================
// EXPORTS
// =============================================================================
// All component types are already exported with their declarations above
